import clsx from 'clsx';
import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import classes from './Carousel.module.scss';

export interface Props {
  children: React.ReactNode;
  ariaLabel: string;
  className?: string;
  viewportClassName?: string;
  containerClassName?: string;
  slideClassName?: string;
  dotsClassName?: string;
  dotClassName?: string;
  activeDotClassName?: string;
  showDots?: boolean;
  renderHeader?: (dots: React.ReactNode) => React.ReactNode;
}

export default function Carousel({
  children,
  ariaLabel,
  className,
  viewportClassName,
  containerClassName,
  slideClassName,
  dotsClassName,
  dotClassName,
  activeDotClassName,
  showDots = true,
  renderHeader,
}: Props) {
  const slides = React.Children.toArray(children).filter(Boolean);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const updateSelectedIndex = React.useCallback(() => {
    if (!emblaApi) {
      return;
    }

    setSelectedIndex(emblaApi.selectedScrollSnap());
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) {
      return;
    }

    updateSelectedIndex();
    emblaApi.on('reInit', updateSelectedIndex);
    emblaApi.on('select', updateSelectedIndex);

    return () => {
      emblaApi.off('reInit', updateSelectedIndex);
      emblaApi.off('select', updateSelectedIndex);
    };
  }, [emblaApi, updateSelectedIndex]);

  if (slides.length === 0) {
    return null;
  }

  const dots =
    showDots && scrollSnaps.length > 1 ? (
      <div className={clsx(classes.dots, dotsClassName)}>
        {scrollSnaps.map((_, index) => (
          <button
            type="button"
            key={index}
            className={clsx(
              classes.dot,
              dotClassName,
              selectedIndex === index && classes.dotActive,
              selectedIndex === index && activeDotClassName,
            )}
            aria-label={`Go to ${ariaLabel} slide ${index + 1}`}
            aria-pressed={selectedIndex === index}
            onClick={() => emblaApi?.scrollTo(index)}
          />
        ))}
      </div>
    ) : null;

  return (
    <div className={clsx(classes.root, className)}>
      {renderHeader ? renderHeader(dots) : null}

      <div className={clsx(classes.viewport, viewportClassName)} ref={emblaRef}>
        <div className={clsx(classes.container, containerClassName)}>
          {slides.map((slide, index) => {
            const key = React.isValidElement(slide) && slide.key != null ? slide.key : index;

            return (
              <div className={clsx(classes.slide, slideClassName)} key={key}>
                {slide}
              </div>
            );
          })}
        </div>
      </div>

      {!renderHeader ? dots : null}
    </div>
  );
}
